import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { auth } from '../lib/auth';
import { billingService } from '../services/billingService';

const FIVE_MIN = 5 * 60; // seconds
const VALIDATION_INTERVAL = 60 * 1000; // 60 seconds - how often to validate session with backend

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null); // { userId, email, phone, exp }
  const [loading, setLoading] = useState(true);
  const [renewPrompt, setRenewPrompt] = useState(false);
  const [error, setError] = useState(null);
  const [billingStatus, setBillingStatus] = useState(null);
  const validationIntervalRef = useRef(null);
  const retryCountRef = useRef(0);

  // Initial session check with timeout
  useEffect(() => {
    let timeoutId;
    let completed = false;

    // Set a timeout to stop loading after 5 seconds and assume logged out
    // This prevents blank screen if backend is cold-starting or unreachable
    timeoutId = setTimeout(() => {
      if (!completed) {
        console.warn('Auth check timed out - showing login screen');
        setSession(null);
        setLoading(false);
        completed = true;
      }
    }, 5000); // 5 second timeout

    (async () => {
      try {
        const me = await auth.me();
        if (!completed) {
          setSession(me);
        }
      } catch (e) {
        if (!completed) {
          setSession(null);
        }
      } finally {
        if (!completed) {
          clearTimeout(timeoutId);
          setLoading(false);
          completed = true;
        }
      }
    })();

    return () => {
      clearTimeout(timeoutId);
      completed = true;
    };
  }, []);

  // Validate session with backend by calling /auth/me
  // This checks the actual cookie, not client-side time
  // Security improvement: validates the cookie server-side instead of trusting client clock
  const validateSession = useCallback(async () => {
    try {
      const me = await auth.me();
      retryCountRef.current = 0; // Reset retry count on success

      // Check if session is expiring soon (using fresh exp from server)
      const nowSec = Math.floor(Date.now() / 1000);
      const ttl = me.exp - nowSec;
      const ttlMinutes = Math.floor(ttl / 60);

      if (ttl <= 0) {
        // Session expired - log out
        console.warn('[Auth] Session expired - logging out');
        setRenewPrompt(false);
        try { await auth.logout(); } catch (_) {}
        setSession(null);
      } else if (ttl <= FIVE_MIN) {
        // Session expiring soon - show renewal prompt
        console.warn(`[Auth] Session expiring soon (${ttlMinutes} min remaining) - showing renewal prompt`);
        setRenewPrompt(true);
        setSession(me); // Update session with fresh data
      } else {
        // Session still valid - hide renewal prompt if showing
        setRenewPrompt(false);
        setSession(me); // Update session with fresh data
      }
    } catch (error) {
      // If we get a 401, session is invalid - log out immediately
      if (error.status === 401) {
        console.warn('[Auth] 401 Unauthorized - logging out');
        setRenewPrompt(false);
        setSession(null);
        return;
      }

      // For network errors, implement exponential backoff
      // Don't log out immediately on network issues
      retryCountRef.current += 1;
      console.warn(`[Auth] Session validation failed (attempt ${retryCountRef.current}):`, error.message);

      // After 3 consecutive failures, log out
      if (retryCountRef.current >= 3) {
        console.error('[Auth] Multiple session validation failures - logging out');
        setRenewPrompt(false);
        try { await auth.logout(); } catch (_) {}
        setSession(null);
      }
    }
  }, []); // Empty deps - uses refs and setters which are stable

  // Fetch billing status whenever session changes
  const refreshBilling = useCallback(async () => {
    try {
      const data = await billingService.getBillingStatus();
      setBillingStatus(data);
    } catch (e) {
      if (e.status === 402) {
        setBillingStatus({ billing_status: 'setup_required', has_payment_method: false });
      } else {
        console.error('[Billing] Failed to fetch billing status:', e);
      }
    }
  }, []);

  const sessionUserId = session?.userId || null;

  // Periodic session validation while logged in
  // Runs every 60 seconds to validate the cookie with the backend
  useEffect(() => {
    // Only validate if user is logged in
    if (!sessionUserId) {
      // Clear interval if it exists
      if (validationIntervalRef.current) {
        clearInterval(validationIntervalRef.current);
        validationIntervalRef.current = null;
      }
      return;
    }

    // Validate immediately on session change (login/user switch)
    validateSession();
    refreshBilling();

    // Then validate periodically every 60 seconds
    validationIntervalRef.current = setInterval(validateSession, VALIDATION_INTERVAL);

    return () => {
      if (validationIntervalRef.current) {
        clearInterval(validationIntervalRef.current);
        validationIntervalRef.current = null;
      }
    };
  }, [sessionUserId, validateSession, refreshBilling]); // Re-run when userId changes

  const value = useMemo(
    () => ({
      session,
      loading,
      renewPrompt,
      error,
      setError,
      billingStatus,
      refreshBilling,
      async login({ email, phone, password, captcha_token }) {
        setError(null);
        await auth.login({ email, phone, password, captcha_token });
        const me = await auth.me();
        setSession(me);
        // Trigger background Plaid sync after login
        // syncAllItems().catch(err => console.error('Background Plaid sync failed:', err));
      },
      async signup({ email, phone, first_name, last_name, password, captcha_token }) {
        setError(null);
        await auth.signup({ email, phone, first_name, last_name, password, captcha_token });
        const me = await auth.me();
        setSession(me);
      },
      async refresh() {
        // Refresh the session by getting a new cookie from backend
        const res = await auth.refresh(); // { ok: true, exp }

        // Get fresh session data from /me
        let me = null;
        try {
          me = await auth.me();
        } catch (_) {
          // If /me fails briefly, still update exp from refresh response
        }

        const newExp = res?.exp || me?.exp || session?.exp || null;
        const base = me || session || {};
        const updated = { ...base, exp: newExp };

        // Update session - this will trigger validateSession via useEffect
        setSession(updated);
        setRenewPrompt(false);
        retryCountRef.current = 0; // Reset retry count on successful refresh
      },
      async logout() {
        try {
          await auth.logout();
        } finally {
          setSession(null);
          // Clear all localStorage on logout
          localStorage.clear();
        }
      },
    }),
    [session, loading, renewPrompt, error, billingStatus, refreshBilling]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
